import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Unified Supabase client plugin (client-side only).
 *
 * **Owns BOTH auth and realtime on a single client instance** — this is the
 * fix for M-3 (realtime auth desync). supabase-js v2 internally wires
 * `onAuthStateChange → realtime.setAuth(accessToken)` on the SAME instance,
 * so two clients would mean two auth states and realtime would stay on anon.
 * See spec §6.2 (Privy→Supabase Auth migration).
 *
 * Consumers across the monorepo:
 * - apps/web — useSupabaseAuth (sign-in flows) + realtime composables
 * - apps/admin — signInWithPassword + chat:global realtime
 * - apps/landing, apps/preview — extend layers/base; auth options inert
 */
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()

  const supabase: SupabaseClient = createClient(
    config.public.supabaseUrl as string,
    config.public.supabaseAnonKey as string,
    {
      auth: {
        persistSession: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
      },
      realtime: {
        params: {
          eventsPerSecond: 20, // Handle high-frequency tick events
        },
      },
    }
  )

  // supabase-js@2.90's _handleTokenChanged calls realtime.setAuth only on
  // SIGNED_IN / TOKEN_REFRESHED / SIGNED_OUT — NOT on INITIAL_SESSION (the
  // event fired exactly once after auth.getSession() rehydrates from
  // localStorage on page reload). Without this, every `private: true`
  // channel joins with the anon key and the realtime server rejects with
  // "Unauthorized" per RLS policy `messages_owner_topics_select`
  // (migration 20260515160000_realtime_messages_rls.sql).
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'INITIAL_SESSION' && session?.access_token) {
      supabase.realtime.setAuth(session.access_token).catch((err) => {
        console.warn('[supabase] realtime.setAuth on INITIAL_SESSION failed:', err)
      })
    }
  })

  return {
    provide: {
      supabase,
    },
  }
})
